import { useRef, useState, type ChangeEvent } from 'react'
import { Box, Button, HStack, Heading, Text, VStack } from '@chakra-ui/react'
import { FiCamera, FiImage, FiTrash2 } from 'react-icons/fi'
import { PersonAvatar } from '../../shared'
import { useDeleteGrantPortrait, useUploadGrantPortrait } from '../../../hooks/useGrantProfile'
import { preparePortrait, PortraitReadError } from '../../../lib/portraitImage'
import { extractApiMessage } from '../../../lib/apiError'

interface PortraitEditorProps {
  name: string
  src: string | null
  isOffline: boolean
}

const READ_ERROR = "That photo couldn't be read. Try a JPEG or PNG."
const SAVE_ERROR = "Couldn't save your photo."

/**
 * [Canvasser redesign Phase 7, P7-6] The canvasser's own photo. No cropper: lib/portraitImage centre-crops
 * and downscales on the phone (all canvas work lives there, off this subtree's no-export ban list). An
 * undecodable file goes up as-is when it fits the cap, and the server's 400 text is shown if it rejects it.
 * Online-only (P7-8), like notes and DNC.
 */
export function PortraitEditor({ name, src, isOffline }: PortraitEditorProps) {
  const uploadMutation = useUploadGrantPortrait()
  const removeMutation = useDeleteGrantPortrait()
  const upload = uploadMutation.mutate
  const remove = removeMutation.mutate
  const isPending = uploadMutation.isPending || removeMutation.isPending
  const takeRef = useRef<HTMLInputElement>(null)
  const chooseRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [isPreparing, setIsPreparing] = useState(false)
  const disabled = isOffline || isPending || isPreparing

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // the same photo can be picked again after an error
    if (!file) return
    setError(null)
    setIsPreparing(true)
    try {
      upload(await preparePortrait(file), {
        onError: err => setError(extractApiMessage(err, SAVE_ERROR)),
      })
    } catch (err) {
      setError(err instanceof PortraitReadError ? READ_ERROR : SAVE_ERROR)
    } finally {
      setIsPreparing(false)
    }
  }

  const onRemove = () => {
    setError(null)
    remove(undefined, { onError: err => setError(extractApiMessage(err, SAVE_ERROR)) })
  }

  return (
    <VStack align="stretch" gap={3}>
      <HStack gap={4} align="center">
        <PersonAvatar name={name} src={src} size="2xl" />
        <Box>
          <Heading size="sm">Your photo</Heading>
          <Text color="fg.muted" fontSize="sm">Helps your organizer and teammates recognise you.</Text>
        </Box>
      </HStack>
      <input ref={takeRef} type="file" accept="image/*" capture="user" hidden onChange={onFile} />
      <input ref={chooseRef} type="file" accept="image/*" hidden onChange={onFile} />
      <HStack gap={2} flexWrap="wrap">
        <Button variant="outline" minH="48px" disabled={disabled} onClick={() => takeRef.current?.click()}>
          <FiCamera /> Take photo
        </Button>
        <Button variant="outline" minH="48px" disabled={disabled} onClick={() => chooseRef.current?.click()}>
          <FiImage /> Choose photo
        </Button>
        {src && (
          <Button variant="ghost" minH="48px" disabled={disabled} onClick={onRemove}>
            <FiTrash2 /> Remove photo
          </Button>
        )}
      </HStack>
      {isOffline && <Text color="fg.muted" fontSize="sm">Connect to the internet to change your photo.</Text>}
      {error && <Text color="danger.fg">{error}</Text>}
    </VStack>
  )
}
